import classNames from 'classnames';
import bindAll from 'lodash.bindall';
import PropTypes from 'prop-types';
import React from 'react';
import {defineMessages, injectIntl, intlShape} from 'react-intl';

import LibraryItem from '../../containers/library-item.jsx';
import Modal from '../../containers/modal.jsx';
import Divider from '../divider/divider.jsx';
import Filter from '../filter/filter.jsx';
import TagButton from '../../containers/tag-button.jsx';
import Spinner from '../spinner/spinner.jsx';
// MSTEM-ADDED Phase 2 GEN-03 — react-tabs for robots-kits mode TabList (DG-02).
// Structural contract per RESEARCH.md §"Render a tab strip" lines 683-711:
// <Tabs> wraps ONE <TabList> (with <Tab> children) PLUS one <TabPanel> sibling
// per <Tab>. Panel count MUST equal Tab count or react-tabs throws at runtime.
import {Tab, Tabs, TabList, TabPanel} from 'react-tabs';
import tabStyles from 'react-tabs/style/react-tabs.css';

import styles from './library.css';

const messages = defineMessages({
    filterPlaceholder: {
        id: 'gui.library.filterPlaceholder',
        defaultMessage: 'Search',
        description: 'Placeholder text for library search field'
    },
    allTag: {
        id: 'gui.library.allTag',
        defaultMessage: 'All',
        description: 'Label for library tag to revert to all items after filtering by tag.'
    }
});

const ALL_TAG = {tag: 'all', intlLabel: messages.allTag};
const tagListPrefix = [ALL_TAG];

class LibraryComponent extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleClose',
            'handleFilterChange',
            'handleFilterClear',
            'handleMouseEnter',
            'handleMouseLeave',
            'handlePlayingEnd',
            'handleSelect',
            'handleSelectTab',
            'handleTagClick',
            'setFilteredDataRef'
        ]);
        // MSTEM-ADDED Phase 2 GEN-03 — activeTabKey drives robots-kits mode filter.
        const defaultTabKey = props.defaultTabKey ||
            (props.tabsConfig && props.tabsConfig.length > 0 ? props.tabsConfig[0].key : null);
        this.state = {
            playingItem: null,
            filterQuery: '',
            selectedTag: ALL_TAG.tag,
            activeTabKey: defaultTabKey,
            loaded: false
        };
    }
    componentDidMount () {
        // Allow the spinner to display before loading the content
        setTimeout(() => {
            this.setState({loaded: true});
        });
        if (this.props.setStopHandler) this.props.setStopHandler(this.handlePlayingEnd);
    }
    componentDidUpdate (prevProps, prevState) {
        if (prevState.filterQuery !== this.state.filterQuery ||
            prevState.selectedTag !== this.state.selectedTag) {
            this.scrollToTop();
        }
    }
    handleSelect (id) {
        if (this.props.autoClose) {
            this.handleClose();
        }
        this.props.onItemSelected(this.getFilteredData()[id]);
    }
    handleClose () {
        this.props.onRequestClose();
    }
    handleTagClick (tag) {
        if (this.state.playingItem === null) {
            this.setState({
                filterQuery: '',
                selectedTag: tag.toLowerCase()
            });
        } else {
            this.props.onItemMouseLeave(this.getFilteredData()[[this.state.playingItem]]);
            this.setState({
                filterQuery: '',
                playingItem: null,
                selectedTag: tag.toLowerCase()
            });
        }
    }
    handleSelectTab (tabIndex) {
        // MSTEM-ADDED Phase 2 GEN-03 — robots-kits mode tab switch.
        if (this.props.tabsConfig && this.props.tabsConfig[tabIndex]) {
            this.setState({
                activeTabKey: this.props.tabsConfig[tabIndex].key,
                filterQuery: '',
                playingItem: null
            });
        }
    }
    handleMouseEnter (id) {
        // don't restart if mouse over already playing item
        if (this.props.onItemMouseEnter && this.state.playingItem !== id) {
            this.props.onItemMouseEnter(this.getFilteredData()[id]);
            this.setState({
                playingItem: id
            });
        }
    }
    handleMouseLeave (id) {
        if (this.props.onItemMouseLeave) {
            this.props.onItemMouseLeave(this.getFilteredData()[id]);
            this.setState({
                playingItem: null
            });
        }
    }
    handlePlayingEnd () {
        if (this.state.playingItem !== null) {
            this.setState({
                playingItem: null
            });
        }
    }
    handleFilterChange (event) {
        if (this.state.playingItem === null) {
            this.setState({
                filterQuery: event.target.value,
                selectedTag: ALL_TAG.tag
            });
        } else {
            this.props.onItemMouseLeave(this.getFilteredData()[[this.state.playingItem]]);
            this.setState({
                filterQuery: event.target.value,
                playingItem: null,
                selectedTag: ALL_TAG.tag
            });
        }
    }
    handleFilterClear () {
        this.setState({filterQuery: ''});
    }
    getFilteredData () {
        const data = this.props.data.filter(device => device.hide !== true);
        // MSTEM-ADDED Phase 2 GEN-03 — robots-kits mode uses tabsConfig.filter
        // instead of the legacy 3-tag filter. Tab predicates own the inclusion
        // logic; this short-circuits BEFORE the existing all-tag / selected-tag
        // branches below.
        if (this.props.mode === 'robots-kits' && this.props.tabsConfig) {
            const activeTab = this.props.tabsConfig.find(t => t.key === this.state.activeTabKey);
            if (activeTab && typeof activeTab.filter === 'function') {
                return data.filter(activeTab.filter);
            }
            return data;
        }
        if (this.state.selectedTag === 'all') {
            if (!this.state.filterQuery) return data;
            return data.filter(dataItem => (
                (dataItem.tags || [])
                    // Second argument to map sets `this`
                    .map(String.prototype.toLowerCase.call, String.prototype.toLowerCase)
                    .concat(dataItem.name ?
                        (typeof dataItem.name === 'string' ?
                        // Use the name if it is a string, else use formatMessage to get the translated name
                            dataItem.name : this.props.intl.formatMessage(dataItem.name.props)
                        ).toLowerCase() :
                        null)
                    .join('\n') // unlikely to partially match newlines
                    .indexOf(this.state.filterQuery.toLowerCase()) !== -1
            ));
        }
        return data.filter(dataItem => (
            dataItem.tags &&
            dataItem.tags
                .map(String.prototype.toLowerCase.call, String.prototype.toLowerCase)
                .indexOf(this.state.selectedTag) !== -1
        ));
    }
    scrollToTop () {
        this.filteredDataRef.scrollTop = 0;
    }
    setFilteredDataRef (ref) {
        this.filteredDataRef = ref;
    }
    render () {
        // MSTEM-ADDED Phase 2 GEN-03 — robots-kits mode (DG-02): swap FilterBar
        // for a TabList of Robot|SmartHome and add a bottom-right Manage button.
        const isRobotsKits = this.props.mode === 'robots-kits' &&
            this.props.tabsConfig && this.props.tabsConfig.length > 0;
        const activeTabIndex = isRobotsKits ?
            this.props.tabsConfig.findIndex(t => t.key === this.state.activeTabKey) : 0;
        const showFilterBar = !isRobotsKits && (this.props.filterable || this.props.tags);

        return (
            <Modal
                fullScreen
                contentLabel={this.props.title}
                id={this.props.id}
                onRequestClose={this.handleClose}
            >
                {isRobotsKits ? (
                    <div className={styles.tabsRow}>
                        <Tabs
                            className={tabStyles.reactTabs}
                            selectedIndex={Math.max(0, activeTabIndex)}
                            onSelect={this.handleSelectTab}
                        >
                            <TabList className={classNames(tabStyles.reactTabsTabList, styles.mstemTabList)}>
                                {this.props.tabsConfig.map(tab => (
                                    <Tab
                                        key={tab.key}
                                        className={classNames(tabStyles.reactTabsTab, styles.mstemTab)}
                                        selectedClassName={styles.mstemTabActive}
                                    >
                                        {tab.label}
                                    </Tab>
                                ))}
                            </TabList>
                            {/* react-tabs requires TabPanel elements as siblings of TabList inside the
                                <Tabs> parent. The panels are intentionally empty — the actual grid is
                                rendered below in libraryScrollGrid, filtered by activeTabKey via
                                getFilteredData(). Tab count and TabPanel count MUST match or react-tabs
                                throws "tabPanel mapping mismatch" at runtime. */}
                            {this.props.tabsConfig.map(tab => (
                                <TabPanel key={`panel-${tab.key}`} />
                            ))}
                        </Tabs>
                    </div>
                ) : (showFilterBar && (
                    <div className={styles.filterBar}>
                        {this.props.filterable && (
                            <Filter
                                className={classNames(
                                    styles.filterBarItem,
                                    styles.filter
                                )}
                                filterQuery={this.state.filterQuery}
                                inputClassName={styles.filterInput}
                                placeholderText={this.props.intl.formatMessage(messages.filterPlaceholder)}
                                onChange={this.handleFilterChange}
                                onClear={this.handleFilterClear}
                            />
                        )}
                        {this.props.filterable && this.props.tags && (
                            <Divider className={classNames(styles.filterBarItem, styles.divider)} />
                        )}
                        {this.props.tags &&
                            <div className={styles.tagWrapper}>
                                {tagListPrefix.concat(this.props.tags).map((tagProps, id) => (
                                    <TagButton
                                        active={this.state.selectedTag === tagProps.tag.toLowerCase()}
                                        className={classNames(
                                            styles.filterBarItem,
                                            styles.tagButton,
                                            tagProps.className
                                        )}
                                        key={`tag-button-${id}`}
                                        onClick={this.handleTagClick}
                                        {...tagProps}
                                    />
                                ))}
                            </div>
                        }
                    </div>
                ))}
                <div
                    className={classNames(styles.libraryScrollGrid, {
                        [styles.withFilterBar]: isRobotsKits || this.props.filterable || this.props.tags
                    })}
                    ref={this.setFilteredDataRef}
                >
                    {this.state.loaded ? this.getFilteredData().map((dataItem, index) => (
                        <LibraryItem
                            author={dataItem.author}
                            bluetoothRequired={dataItem.bluetoothRequired}
                            serialportRequired={dataItem.serialportRequired}
                            programMode={dataItem.programMode}
                            programLanguage={dataItem.programLanguage}
                            collaborator={dataItem.collaborator}
                            description={dataItem.description}
                            disabled={dataItem.disabled}
                            extensionId={dataItem.extensionId}
                            deviceId={dataItem.deviceId}
                            featured={dataItem.featured}
                            helpLink={dataItem.helpLink}
                            hidden={dataItem.hidden}
                            iconMd5={dataItem.costumes ? dataItem.costumes[0].md5ext : dataItem.md5ext}
                            iconRawURL={dataItem.rawURL}
                            icons={dataItem.costumes}
                            id={index}
                            insetIconURL={dataItem.insetIconURL}
                            internetConnectionRequired={dataItem.internetConnectionRequired}
                            intl={this.props.intl}
                            isLoaded={dataItem.isLoaded}
                            isUnloadble={this.props.isUnloadble}
                            isPlaying={this.state.playingItem === index}
                            key={typeof dataItem.name === 'string' ? dataItem.name : dataItem.rawURL}
                            learnMore={dataItem.learnMore}
                            manufactor={dataItem.manufactor}
                            name={dataItem.name}
                            showPlayButton={this.props.showPlayButton}
                            onMouseEnter={this.handleMouseEnter}
                            onMouseLeave={this.handleMouseLeave}
                            onSelect={this.handleSelect}
                            version={dataItem.version}
                        />
                    )) : (
                        <div className={styles.spinnerWrapper}>
                            <Spinner
                                large
                                level="primary"
                            />
                        </div>
                    )}
                </div>
                {/* MSTEM-ADDED Phase 2 GEN-03 / extended GEN-04 — Manage button
                    slot, only in robots-kits mode. GEN-04 makes the slot
                    tab-aware: if manageButtonByTab[activeTabKey] is provided,
                    render that variant (e.g. cyan Kit button on the Kit tab);
                    otherwise fall back to the single manageButton prop for
                    backwards compat. */}
                {isRobotsKits ? (() => {
                    const byTab = this.props.manageButtonByTab;
                    const activeNode = (byTab && byTab[this.state.activeTabKey]) ||
                        this.props.manageButton;
                    return activeNode ? (
                        <div className={styles.manageFooter}>
                            {activeNode}
                        </div>
                    ) : null;
                })() : null}
            </Modal>
        );
    }
}

LibraryComponent.propTypes = {
    autoClose: PropTypes.bool,
    data: PropTypes.arrayOf(
        /* eslint-disable react/no-unused-prop-types, lines-around-comment */
        // An item in the library
        PropTypes.shape({
            // @todo remove md5/rawURL prop from library, refactor to use storage
            md5: PropTypes.string,
            name: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.node
            ]),
            rawURL: PropTypes.string
        })
        /* eslint-enable react/no-unused-prop-types, lines-around-comment */
    ),
    defaultTabKey: PropTypes.string,
    filterable: PropTypes.bool,
    id: PropTypes.string.isRequired,
    intl: intlShape.isRequired,
    isUnloadble: PropTypes.bool,
    manageButton: PropTypes.node,
    // MSTEM-ADDED Phase 2 GEN-04 — tab-aware Manage button slot. Map of
    // tabKey → React node. When present, takes precedence over manageButton
    // for the currently-active tab.
    manageButtonByTab: PropTypes.objectOf(PropTypes.node),
    mode: PropTypes.oneOf(['boards', 'robots-kits']),
    onItemMouseEnter: PropTypes.func,
    onItemMouseLeave: PropTypes.func,
    onItemSelected: PropTypes.func,
    onRequestClose: PropTypes.func,
    setStopHandler: PropTypes.func,
    showPlayButton: PropTypes.bool,
    tabsConfig: PropTypes.arrayOf(PropTypes.shape({
        key: PropTypes.string.isRequired,
        label: PropTypes.node.isRequired,
        filter: PropTypes.func.isRequired
    })),
    tags: PropTypes.arrayOf(PropTypes.shape(TagButton.propTypes)),
    title: PropTypes.string.isRequired
};

LibraryComponent.defaultProps = {
    autoClose: true,
    isUnloadble: false,
    filterable: true,
    mode: 'boards',
    showPlayButton: false
};

export default injectIntl(LibraryComponent);
